import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import EventCard from "./EventCard";
import Reveal from "../../../common_components/Reveal";
import useScrollReveal from "../../../hooks/useScrollReveal";

const EventCarousel = ({ events, title }) => {
  const scrollRef = useRef(null);
  const [stripRef, stripVisible] = useScrollReveal();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [events]);

  const scrollByCards = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.8), behavior: "smooth" });
  };

  const arrowBase =
    "hidden md:flex absolute top-[120px] z-20 w-10 h-10 -translate-y-1/2 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 items-center justify-center text-white cursor-pointer transition-all duration-300 hover:bg-[#17BD3B] hover:border-[#17BD3B] hover:scale-110";

  return (
    <div className="mt-6 sm:mt-8 md:mt-12">
      <Reveal className="flex items-center justify-between mt-[20px] sm:mt-[30px] md:mt-[38px] mb-[20px] sm:mb-[30px] md:mb-[40px]">
        <h2 className="text-white font-['Poppins'] text-[16px] sm:text-[18px] md:text-[20px] leading-[24px] sm:leading-[27px] md:leading-[30px] font-medium">
          {title}
        </h2>
        <Link to="/events-filters">
          <button
            className="bg-transparent border border-white text-white font-medium transition-all duration-300 hover:bg-white/5 hover:border-[#17BD3B] hover:text-[#17BD3B] text-xs sm:text-sm flex justify-center items-center px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 rounded-full sm:rounded-[20px]"
          >
            View All
          </button>
        </Link>
      </Reveal>

      <div ref={stripRef} className="relative group/carousel">
        {/* Edge fades hinting at more content (extend over the strip's -mx breathing room) */}
        <div
          className={`pointer-events-none absolute -left-3 top-0 bottom-4 w-12 sm:w-16 bg-gradient-to-r from-[#111111] to-transparent z-10 transition-opacity duration-300 ${canScrollLeft ? "opacity-100" : "opacity-0"}`}
        />
        <div
          className={`pointer-events-none absolute -right-3 top-0 bottom-4 w-12 sm:w-16 bg-gradient-to-l from-[#111111] to-transparent z-10 transition-opacity duration-300 ${canScrollRight ? "opacity-100" : "opacity-0"}`}
        />

        {/* Arrows appear on hover (desktop) */}
        <button
          onClick={() => scrollByCards(-1)}
          aria-label={`Scroll ${title} left`}
          className={`${arrowBase} left-2 ${canScrollLeft ? "opacity-0 group-hover/carousel:opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          onClick={() => scrollByCards(1)}
          aria-label={`Scroll ${title} right`}
          className={`${arrowBase} right-2 ${canScrollRight ? "opacity-0 group-hover/carousel:opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* pt/-mt and px/-mx give scaled cards room to grow inside the scroll clip
            (overflow-x:auto also clips vertically, so padding is the paint area) */}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className={`flex gap-3 sm:gap-4 md:gap-6 overflow-x-auto pt-5 -mt-5 px-3 -mx-3 pb-4 scrollbar-hide snap-x reveal-stagger ${stripVisible ? "stagger-go" : ""}`}
        >
          {events.slice(0, 5).map((event, index) => (
            <div key={`${event.id}-${index}`} className="snap-start flex-shrink-0" style={{ "--stagger": index }}>
              <EventCard event={event} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventCarousel;
